import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImgUrl } from "@/components/ImageUploader";

const AUTOPLAY_MS = 5000;

/**
 * Full-width hero carousel.
 * - Whole slide image is clickable → navigates to slide.cta_link.
 * - No overlaid heading/subheading/button (image carries all visuals).
 * - <picture>: mobile_image_url below 768px, image_url at 768px+.
 * Props: slides: [{ image_url, mobile_image_url, alt_text, cta_link }]
 */
export default function HeroCarousel({ slides = [], autoplay = true, testid = "hero-carousel", forceVariant = null }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !autoplay || paused || slides.length <= 1) return;
    const id = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, autoplay, paused, slides.length]);

  if (!slides || slides.length === 0) {
    return (
      <div
        className="w-full h-[52vh] min-h-[340px] sm:h-[62vh] sm:min-h-[440px] lg:h-[600px] shimmer bg-slate-200"
        data-testid="hero-skeleton"
        aria-hidden="true"
      />
    );
  }

  const SlideMedia = ({ s, i }) => {
    const priority = i === 0
      ? { loading: "eager", fetchPriority: "high" }
      : { loading: "lazy", fetchPriority: "low" };
    if (forceVariant === "mobile") {
      return (
        <img src={resolveImgUrl(s.mobile_image_url || s.image_url)} alt={s.alt_text || "Promotional banner"}
          width={800} height={600} {...priority}
          className="absolute inset-0 w-full h-full object-cover" />
      );
    }
    if (forceVariant === "desktop") {
      return (
        <img src={resolveImgUrl(s.image_url)} alt={s.alt_text || "Promotional banner"}
          width={1600} height={600} {...priority}
          className="absolute inset-0 w-full h-full object-cover" />
      );
    }
    return (
      <picture>
        <source media="(max-width: 767px)" srcSet={resolveImgUrl(s.mobile_image_url || s.image_url)} />
        <img
          src={resolveImgUrl(s.image_url)}
          alt={s.alt_text || "Promotional banner"}
          width={1600}
          height={600}
          className="absolute inset-0 w-full h-full object-cover"
          {...priority}
        />
      </picture>
    );
  };

  return (
    <section
      className="relative w-full"
      data-testid={testid}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {slides.map((s, i) => {
            const slideClasses =
              "relative flex-[0_0_100%] min-w-0 h-[52vh] min-h-[340px] sm:h-[62vh] sm:min-h-[440px] lg:h-[600px] bg-slate-200 block";
            const common = { "data-testid": `hero-slide-${i}`, className: slideClasses };
            const media = <SlideMedia s={s} i={i} />;
            if (s.cta_link) {
              return s.cta_link.startsWith("http") ? (
                <a key={s.banner_id || i} href={s.cta_link} aria-label={s.alt_text || "Shop this banner"} {...common} data-testid={`hero-slide-${i}`}>
                  {media}
                </a>
              ) : (
                <Link key={s.banner_id || i} to={s.cta_link} aria-label={s.alt_text || "Shop this banner"} {...common} data-testid={`hero-slide-${i}`}>
                  {media}
                </Link>
              );
            }
            return (
              <div key={s.banner_id || i} {...common}>
                {media}
              </div>
            );
          })}
        </div>
      </div>

      {slides.length > 1 && (
        <>
          {/* Arrows — smaller/subtler on mobile, full-size on desktop; swipe works everywhere */}
          <button
            type="button" onClick={scrollPrev} aria-label="Previous slide" data-testid="hero-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-white/80 sm:bg-white/90 hover:bg-white text-slate-900 shadow-md sm:shadow-lg flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6"/>
          </button>
          <button
            type="button" onClick={scrollNext} aria-label="Next slide" data-testid="hero-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-white/80 sm:bg-white/90 hover:bg-white text-slate-900 shadow-md sm:shadow-lg flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6"/>
          </button>

          <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button
                key={i} type="button" onClick={() => scrollTo(i)} aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${selected === i ? "w-7 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
